import { useState, useEffect } from "react";
import { Container, Text, Stack, Center, Loader, Box, Button } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconBookmark } from "@tabler/icons-react";
import { successNotification } from "../../shared/utils/successNotification";
import trpc from "../../shared/utils/trpc";
import { handleTrpcError } from "../../shared/utils/trpc/handletrpcError.util.ts";
import CompanyCardHorizontal, { CompanyWithUI } from "../../components/shared/CompanyCardHorizontal";
import GenericModal from "../../components/shared/GenericModal";
import ExpressInterestModal, { ExpressInterestFormData } from "../../components/shared/ExpressInterestModal";
import { useNavigate } from "react-router-dom";
import { useUser } from "@/context/UserContext";
import { getIndustryLabel } from "../../components/shared/constants/general.const";
import TitleComponent from "../../components/shared/TitleComponent";
import AppPaginationBar from "@/components/shared/AppPaginationBar";

const PAGE_SIZE = 10;

export default function ShortlistPage() {
  const navigate = useNavigate();
  const { userDetails, removeFromShortlist, refreshPendingInterestCount } = useUser();

  const [shortlistedCompanies, setShortlistedCompanies] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCompanies, setTotalCompanies] = useState(0);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [removeModalOpened, { open: openRemoveModal, close: closeRemoveModal }] = useDisclosure(false);
  const [companyToRemove, setCompanyToRemove] = useState<string | null>(null);

  const [expressInterestModalOpened, { close: closeExpressInterestModal }] = useDisclosure(false);
  const [companyToExpressInterest, setCompanyToExpressInterest] = useState<string | null>(null);

  const MAX_RETRIES = 3;

  const fetchShortlist = async () => {
    if (isLoading) return;
    if (retryCount >= MAX_RETRIES) return;

    setIsLoading(true);
    setLoadError(null);

    try {
      const response = await trpc.user.getshortlist.query({
        page,
        limit: PAGE_SIZE,
      });

      setShortlistedCompanies(response.companies);
      setTotalPages(response.pagination?.totalPages || 1);
      setTotalCompanies(response.pagination?.total || response.companies?.length || 0);
      setRetryCount(0);
      setLoadError(null);
      setError(false);
    } catch (err) {
      handleTrpcError(err);
      const newRetryCount = retryCount + 1;
      setRetryCount(newRetryCount);

      if (newRetryCount >= MAX_RETRIES) {
        setLoadError("Unable to load more companies. Please check your connection and try again later.");
        setError(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: "smooth" });
    fetchShortlist();
  }, [page]);

  if (error && shortlistedCompanies.length === 0) {
    return (
      <Center mih="50vh">
        <Text c="red">Failed to load shortlist</Text>
      </Center>
    );
  }

  const handleRemoveShortlist = async (companyId: string, isShortlisted: boolean) => {
    if (!isShortlisted) {
      // Open confirmation modal
      setCompanyToRemove(companyId);
      openRemoveModal();
    }
  };

  const confirmRemoveShortlist = async () => {
    if (!companyToRemove) return;

    try {
      await removeFromShortlist(companyToRemove);

      successNotification({
        title: "Success",
        message: "Company removed from shortlist",
      });

      fetchShortlist();
    } catch (err) {
      handleTrpcError(err);
    } finally {
      closeRemoveModal();
      setCompanyToRemove(null);
    }
  };

  const confirmExpressInterest = async (data: ExpressInterestFormData) => {
    if (!companyToExpressInterest) return;

    try {
      await trpc.collaboration.request.mutate({
        targetCompanyId: companyToExpressInterest,
        requesterRole: data.requesterRole,
        expectedPartnerRole: data.expectedPartnerRole,
      });

      refreshPendingInterestCount();
      successNotification({
        title: "Success",
        message: "Your interest has been expressed",
      });
    } catch (err) {
      handleTrpcError(err);
    } finally {
      closeExpressInterestModal();
      setCompanyToExpressInterest(null);
    }
  };

  const companies: CompanyWithUI[] = (shortlistedCompanies || []).map((item: any) => ({
    ...item,
    name: item.legalName, // Map for UI if needed, though card uses legalName now
    verified: item.status === "VERIFIED",
    industry:
      Array.isArray(item.industryCategory) && item.industryCategory.length > 0
        ? item.industryCategory.map((c: string) => getIndustryLabel(c)).join(", ")
        : "N/A",
    locations: item.headquartersCountry ? [item.headquartersCountry] : [],
    capabilities: item.coreCapabilities || [],
    isShortlisted: true,
  }));

  return (
    <Box mih="100vh" pb={20}>
      <TitleComponent title="Shortlisted Companies" description="Manage your saved companies and take next steps." />

      <Container fluid pb={40} px="md">
        {companies.length === 0 && !isLoading ? (
          <Stack align="center" py="xl">
            <Text c="slate.6">No companies shortlisted yet.</Text>
            <Button
              variant="outline"
              onClick={() => navigate("/my-discovery")}
              leftSection={<IconBookmark size={16} />}
            >
              Go to Discover
            </Button>
          </Stack>
        ) : (
          <Stack gap="md">
            {companies.map((company) => (
              <CompanyCardHorizontal
                key={company.id}
                company={company}
                isDiscoverStyle
                onShortlistClick={handleRemoveShortlist}
                onViewDetails={(id) => navigate(`/shortlist/${id}`)}
                isShortlist
                showShortlistButton
              />
            ))}
          </Stack>
        )}

        {isLoading && (
          <Center py="md">
            <Loader size="sm" />
          </Center>
        )}

        {loadError && (
          <Center py="md">
            <Text c="red" size="sm">
              {loadError}
            </Text>
          </Center>
        )}

        {totalPages > 1 && (
          <Box mt="xl">
            <AppPaginationBar
              page={page}
              totalPages={totalPages}
              totalItems={totalCompanies}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
              pageSizeOptions={[PAGE_SIZE]}
              boundaries={3}
            />
          </Box>
        )}
      </Container>

      <GenericModal
        disclosure={[
          removeModalOpened,
          {
            open: openRemoveModal,
            close: () => {
              closeRemoveModal();
              setCompanyToRemove(null);
            },
            toggle: () => {},
          },
        ]}
        title="Remove Shortlist"
        subtitle="Are you sure you want to remove this company from your shortlist?"
        buttons={[
          {
            label: "Remove",
            onClick: confirmRemoveShortlist,
            variant: "filled",
            color: "primary",
          },
        ]}
      />

      <ExpressInterestModal
        opened={expressInterestModalOpened}
        onClose={() => {
          closeExpressInterestModal();
          setCompanyToExpressInterest(null);
        }}
        onConfirm={confirmExpressInterest}
        loading={false}
        userWhoAmI={(userDetails?.company?.whoAmI as string[]) || []}
        targetCollaborationIntent={
          (shortlistedCompanies.find((c: any) => c.id === companyToExpressInterest)?.collaborationIntent as string[]) ||
          []
        }
      />
    </Box>
  );
}
